import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BaseScreen } from "../../components/BaseScreen.tsx";
import ErrorMessage from "../../components/ErrorMessage.tsx";
import Text from "../../components/Text.tsx";
import { useMParticle } from "../../hooks/useMparticle.ts";
import { useTranslations } from '../../hooks/useTranslations.ts';
import {
  API_ERROR_MESSAGES,
  apiIdentitySearch,
  ApiIdentitySearchResult,
  IdentityStatus,
} from "../../onboarding-api";
import { translationKeys } from '../../translations/translationKeys.ts';
import { SignUpState } from "../SignUpJourney.tsx";

type Props = {
  signUpState: SignUpState;
  onCompleted: (
    email: string,
    status: IdentityStatus,
    csrfToken: string
  ) => void;
  onGoBack?: () => void;
};

function validateEmail(email: string) {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
}

export const EmailScreen: FC<Props> = ({ signUpState, onCompleted, onGoBack }) => {
  const [email, setEmail] = useState<string>(signUpState.email);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { translate } = useTranslations();
  const { logRegistrationStart, logEmailAlreadyExists } = useMParticle();

  const emailIsValid = useMemo(() => {
    return validateEmail(email);
  }, [email]);
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined
  );
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isDisabled = !emailIsValid || isSubmitting;

  useEffect(() => {
    // Focus the input when the component mounts
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (validationError && emailIsValid) {
      setValidationError(undefined);
    }
  }, [emailIsValid, validationError]);

  const onBlur = useCallback(() => {
    if (!emailIsValid) {
      setValidationError(translate(translationKeys.VALID_EMAIL_ADDRESS_MESSAGE));
    } else {
      setValidationError(undefined);
    }
  }, [emailIsValid, translate]);

  const onSubmit = useCallback(async () => {
    if (!isDisabled) {
      setError(null);
      setIsSubmitting(true);

      const { data, errorCode, errorMessage }: ApiIdentitySearchResult =
        await apiIdentitySearch(email);
      if (data) {

        if (data.status === IdentityStatus.new) {
          logRegistrationStart(email);
        } else if (data.status === IdentityStatus.verified || data.status === IdentityStatus.unverified) {
          logEmailAlreadyExists();
        }

        onCompleted(email, data.status, data.csrfToken);
      } else if (errorCode) {
        setError(errorMessage || API_ERROR_MESSAGES[errorCode]);
      }

      setIsSubmitting(false);
    }
  }, [email, isDisabled, onCompleted, logRegistrationStart, logEmailAlreadyExists]);
  return (
    <BaseScreen
      formTitle={translate(translationKeys.EMAIL_SCREEN_TITLE)}
      formSubtitle={
        translate(translationKeys.EMAIL_SCREEN_SUBTITLE)
      }
      footerBtnLabel={translate(translationKeys.FOOTER_BUTTON_LABEL)}
      footerBtnOnClick={onSubmit}
      footerBtnLoading={isSubmitting}
      footerBtnDisabled={!emailIsValid}
      headerBackBtnVisible={!!onGoBack}
      headerBackBtnOnClick={onGoBack}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Text
          ref={(el) => {
            inputRef.current = el;
          }}
          label={translate(translationKeys.EMAIL_INPUT_LABEL)}
          placeholder={translate(translationKeys.EMAIL_INPUT_PLACEHOLDER)}
          id="email"
          fullWidth={true}
          value={email}
          updateFieldValue={setEmail}
          triggerFieldChange={() => {}}
          onBlur={onBlur}
          name="email"
          disabled={isSubmitting}
          validatemessage={validationError}
        />
      </form>
      {/* TODO MOVE TO TRANSLATE */}
      <p>By proceeding, I agree to the demo platform <a href="https://www.cmcmarkets.com/en-gb/important-information" target="_blank" rel="noopener noreferrer">terms of use</a> and <a href="https://www.cmcmarkets.com/en-gb/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>. I agree that CMC Markets has a legitimate interest in contacting me in relation to services available to me and for marketing. I understand that I can opt out of the marketing immediately by contacting the <a href="https://www.cmcmarkets.com/en-gb/contact-us" target="_blank" rel="noopener noreferrer">customer service team</a> or via privacy settings on the platform.</p>
      {error && <ErrorMessage message={error} />}
    </BaseScreen>
  );
};
